import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Loader } from "@fluentui/react-northstar";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  dialog: {
    boxShadow: "-10px 12px 40px rgba(121, 121, 121, 0.12)",
    borderRadius: "10px !important",
    padding: "16px 24px 24px 24px",
  },
});

export const RenameDialog = ({
  handleClose,
  handleRename,
  open,
  loading,
  initialName,
  isFolder,
}) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  const [newName, setNewName] = React.useState("");

  React.useEffect(() => {
    if (open && initialName) {
      if (!isFolder && initialName.includes(".")) {
        const lastDotIndex = initialName.lastIndexOf(".");
        setNewName(initialName.substring(0, lastDotIndex));
      } else {
        setNewName(initialName);
      }
    }
  }, [open, initialName, isFolder]);

  const classes = useStyles();

  const handleSubmit = () => {
    if (newName.trim() && !loading) {
      let finalName = newName.trim();
      if (!isFolder && initialName.includes(".")) {
        const extension = initialName.substring(initialName.lastIndexOf("."));
        finalName += extension;
      }
      handleRename(finalName);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? null : handleClose}
      disableEscapeKeyDown={loading}
      aria-labelledby="rename-dialog-title"
      fullWidth
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle id="rename-dialog-title">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              height: 64,
              width: 64,
              background: "#F5F8FF",
              borderRadius: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <svg
              style={{ height: 28, width: 22 }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#4285F4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </Box>
        </Box>
        <Typography
          variant="h3"
          fontWeight={600}
          mt={2}
          sx={{
            textAlign: "center",
            fontSize: 16,
            lineHeight: "20px",
          }}
        >
          {isFolder ? translation.renameFolder : translation.renameFile}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          display={"grid"}
          alignItems="center"
          justifyContent={"center"}
          mt={1}
          mb={1}
        >
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={translation.enterNewName || "Enter new name"}
            type="text"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: "#EAEEF4" }} />

      <Box
        sx={{
          pt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            if (!loading) handleClose();
          }}
          disabled={loading}
          sx={{
            mr: 2,
            color: "black",
            fontSize: 12,
            background: "#ffffff",
            height: 32,
            border: `1px solid #EAEEF4`,
            fontWeight: 600,
            width: 90,
            ":hover": {
              background: "#f5f5f5",
            },
          }}
        >
          {translation.cancel || "Cancel"}
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleSubmit();
          }}
          disabled={loading || !newName.trim()}
          sx={{
            color: "#ffff",
            fontSize: 12,
            background: "#4285F4",
            height: 32,
            fontWeight: 600,
            width: 90,
            ":hover": {
              background: "#3367d6",
            },
          }}
        >
          {loading ? <Loader size="small" /> : translation.rename || "Rename"}
        </Button>
      </Box>
    </Dialog>
  );
};
